import { useEffect, useRef, useState } from "react";
import { searchableQuery } from "@domain/CRM/job";
import { highlightMatches, type SearchResponse, type SearchResult, type TextSegment } from "@domain/Search";
import { API } from "@gui/infrastructure/pass-through/api";

const DEBOUNCE_MS = 50;
const RESULT_LIMIT = 10;

/** A result as the dropdown holds it. */
export type JobLookupRow = SearchResult & {
	/** What MUI compares rows by, and unique to the response. When the row count changes under unchanged text, MUI looks
	 *  for its old highlight by label. A match, by a recurring title or a row that survived, moves the index Enter acts
	 *  on and leaves the mark on the top row. This has to sit on the row: the lookup reads the old row's label afresh, so
	 *  a generation held in a closure would match, and so would an id, for a row that survived. */
	rowLabel: string;
	/** The title and subtitle split around what matched. Worked out with the rows from their own response's terms, so
	 *  they mark what the rows answer and not newer text that is still out, or the box's syntax. */
	titleParts: TextSegment[];
	subtitleParts: TextSegment[];
};

interface Found {
	/** The text this answers. */
	query: string | null;
	options: JobLookupRow[];
	notice: string | null;
	/** A failed or not-ready answer is worth asking for again. */
	outcome: "answered" | "failed" | "notReady";
}

const NOTHING_FOUND: Found = { query: null, options: [], notice: null, outcome: "answered" };
const SEARCHING_NOTICE = "Searching jobs…";

const failedSearch = (query: string): Found => ({
	...NOTHING_FOUND,
	query,
	notice: "Couldn't search jobs. Refocus the box to try again, or paste a record ID or Airtable link.",
	outcome: "failed",
});

function foundFrom(response: SearchResponse, query: string, responseSeq: number): Found {
	if (!response.ready) {
		return {
			...NOTHING_FOUND,
			query,
			notice: "Try again shortly, or paste a record ID or Airtable link.",
			outcome: "notReady",
		};
	}
	if (response.incomplete) return { ...NOTHING_FOUND, query, notice: "Add a word to search for." };
	if (response.results.length === 0) return { ...NOTHING_FOUND, query, notice: `No jobs match "${query}".` };
	const options = response.results.map((row) => ({
		...row,
		rowLabel: `${responseSeq}:${row.id}`,
		titleParts: highlightMatches(row.title, response.terms),
		subtitleParts: highlightMatches(row.subtitle, response.terms),
	}));
	return { ...NOTHING_FOUND, query, options };
}

interface JobLookupSearchArgs {
	/** What was typed. Never the box's `value` as a host set it: the toolbar's box is filled by the route. */
	text: string;
	enabled: boolean;
}

/**
 * The dropdown behind the job box: what to search for, what came back, and whether it shows.
 *
 * `query` is worked out in the render, and only the request waits for typing to pause, so rows
 * can't outlive a pasted id and catch its Enter. While the answer on hand is for other text it
 * is not `settled`: its rows stay painted so the list doesn't flicker, and its notice gives way.
 */
export const useJobLookupSearch = ({ text, enabled }: JobLookupSearchArgs) => {
	const query = enabled ? searchableQuery(text) : null;
	const [found, setFound] = useState(NOTHING_FOUND);
	const [focused, setFocused] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [retry, setRetry] = useState(0);
	const responseSeq = useRef(0);

	useEffect(() => {
		if (query === null) {
			setFound(NOTHING_FOUND);
			return;
		}
		// A stale response is dropped rather than aborted: axios cancels reach the client
		// interceptor's error report.
		let cancelled = false;
		const timer = setTimeout(() => {
			API.Search.search(query, RESULT_LIMIT).then(
				(response) => {
					if (!cancelled) setFound(foundFrom(response, query, ++responseSeq.current));
				},
				() => {
					if (!cancelled) setFound(failedSearch(query));
				},
			);
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query, retry]);

	const shown = query === null ? NOTHING_FOUND : found;
	const settled = found.query === query;
	const notice = shown.notice !== null && !settled ? SEARCHING_NOTICE : shown.notice;
	const { options } = shown;
	const dropdownOpen = focused && !dismissed && query !== null && (options.length > 0 || notice !== null);
	// Off with the dropdown: a late failure on a box left behind is nobody's news. Off while searching too: the box
	// says so, and announcing it would read out every keystroke.
	const noticeState =
		!dropdownOpen || notice === null || !settled ? "ready" : shown.outcome === "failed" ? "error" : "empty";

	const retryUnanswered = () => {
		if (found.outcome === "answered") return;
		// Unsettled and open, so the retry shows: a blur dismissed the dropdown, and the same notice coming back would
		// look like nothing happened.
		setFound((current) => ({ ...current, query: null }));
		setDismissed(false);
		setRetry((count) => count + 1);
	};

	return {
		options,
		notice,
		noticeState,
		settled,
		dropdownOpen,
		onFocus: () => {
			setFocused(true);
			retryUnanswered();
		},
		onBlur: () => setFocused(false),
		reopen: () => {
			setDismissed(false);
			retryUnanswered();
		},
		dismiss: () => setDismissed(true),
	} as const;
};
